<!-- Show startup loader during initialization -->
@if (initializing()) {
<app-startup-loader [message]="initializationMessage()" />
}

<!-- Show paused overlay when this tab is not the DB leader -->
@if (syncService.isPaused()) {
<app-paused-overlay />
}

<app-toolbar data-testid="app-toolbar">
  @if (authService.isAuthenticated()) {
  <app-button variant="icon" (click)="sidenav().toggle()" class="nav-icon"
    [attr.aria-label]="'nav.openMenu' | translate" data-testid="nav-icon">
    <app-icon name="menu" />
  </app-button>
  }
  <span class="logo-wrapper clickable" (click)="navigateToMap()" (keydown.enter)="navigateToMap()"
    (keydown.space)="navigateToMap()" tabindex="0" role="button" [attr.aria-label]="'nav.goToMap' | translate"
    data-testid="app-title">
    <img [src]="'logos/app-logo-horizontal.png' | envPrefix" [attr.alt]="title()"
      class="logo logo-sm logo-horizontal" />
    <img [src]="'logos/app-icon.png' | envPrefix" [attr.alt]="title()" class="logo logo-sm logo-icon" />
  </span>
  @if (authService.isAuthenticated()) {
  <app-breadcrumb></app-breadcrumb>
  }
  <span class="spacer"></span>
  @if (authService.isAuthenticated() && onMapPage()) {
  <app-map-mode-toggle />
  @if (!environment.production && mapZoomLabel()) {
  <span class="map-zoom-readout" data-testid="map-zoom-readout">{{ mapZoomLabel() }}</span>
  }
  }
  @if (!authService.isAuthenticated()) {
  <app-button variant="icon" (click)="toggleStreamLogs()" data-testid="send-logs-button"
    [tippy]="streamLogsEnabled() ? 'Stop streaming logs to backend' : 'Start streaming logs to backend'"
    [class.active-streaming]="streamLogsEnabled()">
    <app-icon [name]="streamLogsEnabled() ? 'radio' : 'upload'" />
  </app-button>
  }
  @if (authService.isAuthenticated()) {
  <div class="user-menu-dropdown" [class.open]="userDropdownOpen()" [clickOutsideEnabled]="userDropdownOpen()" (clickOutside)="closeUserDropdown()">
    <div class="user-menu" (click)="toggleUserDropdown()" (keydown.enter)="toggleUserDropdown()"
      (keydown.space)="toggleUserDropdown()" tabindex="0" role="button">
      <img [src]="getUserAvatar()" [alt]="userService.currentUser()?.full_name || ''" class="user-avatar"
        data-testid="user-avatar" />
      <div class="user-info">
        <span class="user-name" data-testid="user-name">{{ userService.currentUser()?.full_name }}</span>
        @if (organizationService.currentOrganization()) {
        <span class="org-name" data-testid="org-name">{{ organizationService.currentOrganization()?.name }}</span>
        }
      </div>
    </div>
    <div class="user-dropdown-menu">
      <div class="dropdown-user-info">
        <span class="dropdown-user-name" data-testid="dropdown-user-name">{{ userService.currentUser()?.full_name
          }}</span>
        @if (organizationService.currentOrganization()) {
        <span class="dropdown-org-name" data-testid="dropdown-org-name">{{
          organizationService.currentOrganization()?.name
          }}</span>
        }
      </div>
      <button class="dropdown-item" (click)="toggleStreamLogsAndCloseDropdown()" data-testid="send-logs-menu-item"
        [class.active-streaming]="streamLogsEnabled()">
        <app-icon [name]="streamLogsEnabled() ? 'radio' : 'upload'" />
        <span>{{ streamLogsEnabled() ? 'Stop Streaming Logs' : 'Stream Logs to Backend' }}</span>
      </button>
      <button class="dropdown-item" (click)="logout()" data-testid="logout-button">
        <app-icon name="log-out" />
        <span>{{ 'nav.logout' | translate }}</span>
      </button>
    </div>
  </div>
  }
</app-toolbar>

@if (authService.isAuthenticated()) {
<app-sidenav>
  <nav sidenav class="nav-list nav-section">
    <a app-nav-list-item routerLink="/home">
      <img [src]="'logos/app-icon.png' | envPrefix" [attr.alt]="'nav.home' | translate" class="home-icon" />
      <span>{{ 'nav.home' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/map">
      <app-icon name="map" />
      <span>{{ 'nav.map' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/my-day" *hasPermission="'interaction.read'">
      <app-icon name="sunrise" />
      <span>{{ 'nav.myDay' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/map/addresses" *hasPermission="'address.read'">
      <app-icon name="map-pin" />
      <span>{{ 'nav.addresses' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/leads" *hasPermission="'lead.read'">
      <app-icon name="star" />
      <span>{{ 'nav.leads' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/reports" *hasPermission="'report.view'">
      <app-icon name="chart-no-axes-column-increasing" />
      <span>{{ 'nav.reports' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/leaderboard" *hasPermission="'report.view'">
      <app-icon name="trophy" />
      <span>{{ 'nav.leaderboard' | translate }}</span>
    </a>
    @if (!permissionService.hasAny(['org.settings'])) {
    <a app-nav-list-item routerLink="/settings/organization">
      <app-icon name="building" />
      <span>{{ 'nav.switchOrganization' | translate }}</span>
    </a>
    }
    <a app-nav-list-item routerLink="/settings" *hasPermission="'org.settings'">
      <app-icon name="settings" />
      <span>{{ 'nav.settings' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/diagnostics" *hasPermission="'diagnostics.view'">
      <app-icon name="activity" />
      <span>{{ 'nav.diagnostics' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/dev/sql" *hasPermission="'sql.view'">
      <app-icon name="code" />
      <span>{{ 'nav.devSqlConsole' | translate }}</span>
    </a>
  </nav>

  <!-- Info section - passive information -->
  <div class="flex flex-column flex-grow flex-justify-end" sidenav>
    <div class="info-section flex flex-align-center flex-justify-space-between gap-sm">
      <div class="info-item" data-testid="online-badge-sidenav">
        <app-network-status-badge />
      </div>
      <div class="info-item build-info" data-testid="build-info-sidenav">
        {{ buildTimestamp | dateTimezone }}
      </div>
    </div>
  </div>


  <router-outlet></router-outlet>
</app-sidenav>
} @else {
<div class="main-content">
  <router-outlet></router-outlet>
</div>
}

<app-toast />

<!-- Database reset dialog (shown when backend sync generation changes) -->
@if (showDatabaseResetDialog()) {
<app-database-reset-dialog (reset)="handleDatabaseReset($event)" />
}

<!-- Global entity list selector dialog -->
<app-entity-list-selector-dialog />

<!-- PWA install modal (shown for uninstalled users on a 3-day snooze cycle) -->
@if (pwaInstallService.showModal()) {
<app-pwa-install-modal />
}

<!-- Dynamic modal system -->
<app-modal-container />